import { makeAutoObservable } from "mobx";
import { debounce, DebounceCounter } from "src/common/debounce";
import { onError } from "src/common/onError";
import { Scalar } from "src/types/Scalar";

import { cmpScalarValues } from "../../../../common/cmpScalarValues";
import { getScalarValue } from "./components/dataUtils.ts/getScalarValue";
import { dataNode2Value, ZAttrValue, ZRecord } from "./types/dataTypes";
import { pathDiv, replaceLastPathName } from "./types/fieldPath";
import {
  FormField,
  FormItem,
  FormItemKey,
  toField,
} from "./types/MdmCardFormTypes";
import { ZExprBool } from "./types/metaExpr";

/* eslint no-restricted-syntax: "off" */
/* eslint no-await-in-loop: "off" */

export class MdmCardFormStore {
  constructor() {
    makeAutoObservable(this);
  }

  metaRoot: FormItem | null = null;

  setMetaRoot(item: FormItem | null) {
    this.setErrors({});
    this.metaRoot = item;
  }

  get fields(): Record<string, FormField> {
    const dict: Record<string, FormField> = {};
    this.metaRoot?.walk({
      onField: (field) => {
        dict[field.path] = field;
      },
    });
    return dict;
  }

  get items(): Record<FormItemKey, FormItem> {
    const dict: Record<FormItemKey, FormItem> = {};
    this.metaRoot?.walk({
      onItem: (item) => {
        dict[item.key] = item;
      },
    });
    return dict;
  }

  get visibleKeys(): Set<FormItemKey> {
    const result = new Set<FormItemKey>();
    this.metaRoot?.walk({
      onItem: (item) => {
        const { key, ownerKey, meta } = item;
        if (!ownerKey || result.has(ownerKey)) {
          const expr = meta.hideIf;
          if (expr) {
            const isHidden = this.calcBoolExpr(expr, toField(item) ?? null);
            if (isHidden) return;
          }
          result.add(key);
        }
      },
    });
    return result;
  }

  dataRoot: ZRecord = [];

  setDataRoot(data: ZRecord) {
    this.dataRoot = data;
    data.forEach((it) => {
      const field = this.fields[it.name];
      field?.setData(it);
    });
  }

  updateFields() {
    // Есть подозрение, что эта фича устарела и больше не используется
    this.setDataRoot([...this.dataRoot]);
  }

  get values(): ZRecord {
    const record: ZRecord = [];
    this.metaRoot?.walk({
      onField({ path, data }) {
        if (!path.includes("/") && data) {
          record.push(data);
        }
      },
    });
    return record;
  }

  disabled = false;

  setDisabled(value: boolean) {
    this.disabled = value;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChange(path: string) {
    debounce(validationCounter, 50, () => this.startValidating());
  }

  errors: Record<string, Error> = {};

  setErrors(map: Record<string, Error>) {
    this.errors = map;
  }

  startValidating() {
    // TODO: нужно дождаться окончания предыдущего цикла валидации или даже прервать его
    const newErrors: Record<string, Error> = {};
    const validateField = async (field: FormField): Promise<void> => {
      try {
        // Валидаторы выполняются последовательно
        for (const v of field.validators) {
          await v.test();
        }
      } catch (error) {
        newErrors[field.path] = error as Error;
      }
    };
    const { visibleKeys } = this;
    const fieldsList = Object.values(this.fields).filter(({ key }) =>
      visibleKeys.has(key),
    );
    Promise.all(fieldsList.map(validateField))
      .catch(onError)
      .then(() => {
        this.setErrors(newErrors);
      });
  }

  isRequiredField(path: string): boolean {
    return this.fields[path]?.validators.some((v) => v.isRequired) ?? false;
  }

  //

  getFieldValue(path: string): ZAttrValue | undefined {
    const field = this.fields[path];
    const data = field?.data;
    if (data) {
      return dataNode2Value(data);
    }
    return undefined;
  }

  evaluateText(
    srcText: string | null | undefined,
    fieldOrAbsPath?: FormField | string,
  ): string {
    if (!srcText) return "";
    let currentField: FormField | undefined;
    if (typeof fieldOrAbsPath === "object") {
      currentField = fieldOrAbsPath;
    } else if (typeof fieldOrAbsPath === "string") {
      currentField = this.fields[fieldOrAbsPath];
    }
    let dstText = srcText;
    const str = (s: Scalar): string => (s === null ? "" : String(s));
    for (;;) {
      const res = /{{([^}]*)}}/.exec(dstText);
      if (!res) break;
      const expr = res[1];
      dstText = dstText.replace(
        `{{${expr}}}`,
        str(this.calcExprValue(expr, currentField ?? null)),
      );
    }
    return dstText;
  }

  /**
   * Разобрать строковое выражение, которое содержит указание на поле и суффикс
   * @param expr
   * @param field
   * @returns Если не найдено, то null. Иначе поле и суффикс
   */
  findFieldExt(
    expr: string | undefined,
    field: FormField | null,
  ): [FormField, string | null] | null {
    if (!expr) return null;
    const stop = expr.indexOf(".");
    const [pathExt, suffix] =
      stop < 0 ? [expr, null] : [expr.slice(0, stop), expr.slice(stop)];
    let tmpPath = pathExt;
    if (field && (pathExt[0] === "^" || pathExt[0] === "@")) {
      let absPath = field.path;
      while (tmpPath[0] === "^") {
        tmpPath = tmpPath.slice(1);
        const segs = absPath.split(pathDiv);
        absPath = segs.slice(0, -2).join(pathDiv);
      }
      if (tmpPath[0] === "@") {
        if (!field) return null;
        tmpPath = replaceLastPathName(absPath, tmpPath.slice(1));
      }
    }

    const finalField = this.fields[tmpPath];
    return finalField ? [finalField, suffix] : null;
  }

  /**
   * Получить скалярное значение для указанного выражения
   * Примеры выражений:
   * - "fieldName" - название поле из корневой записи, возвращает значение поля
   * - "fieldName.label" - для значений типа Option возвращает label выбранной записи
   * - "fieldName.title" - название указанного поля
   * - "@fieldName" - для вложенных значений указывает соседнее поле, которое находится в той же записи
   * - "^@fieldName" - указывает поле, которое находится на уровень выше
   * @param expr
   * @param field
   * @returns null, если не найдено
   */
  calcExprValue(expr: string | undefined, field: FormField | null): Scalar {
    const res = this.findFieldExt(expr, field);
    if (!res) return null;
    const [finalField, ext] = res;
    if (ext === ".title") {
      return this.evaluateText(finalField.meta.title);
    }
    const aValue = finalField?.data;
    if (ext === ".label" && aValue?.type === "strOption") {
      return aValue.value.label;
    }
    return getScalarValue(finalField?.data);
  }

  calcBoolExpr(expr: ZExprBool, field: FormField | null): boolean {
    let result: boolean = false;
    switch (expr.type) {
      case "true":
        result = true;
        break;
      case "boolField":
        result = !!this.calcExprValue(expr.path, field);
        if (expr.inv) result = !result;
        break;
      case "fieldValue":
        {
          const leftValue = this.calcExprValue(expr.path, field);
          result = !!cmpScalarValues(expr.op, leftValue, expr.value);
        }
        break;
      default:
        break;
    }
    return result;
  }

  isItemDisabled(item: FormItem): boolean {
    const { meta, ownerKey } = item;
    const owner = ownerKey === null ? null : this.items[ownerKey];
    const ownerDisabled = owner ? this.isItemDisabled(owner) : this.disabled;
    if (ownerDisabled) return true;

    const { type, disableIf } = meta;
    if (!disableIf) return false;
    return this.calcBoolExpr(
      disableIf,
      type === "field" ? (item as FormField) : null,
    );
  }

  getOwnerItems(path: string): FormItem[] {
    const items: FormItem[] = [];
    let curItem: FormItem | undefined = this.fields[path];
    while (curItem) {
      items.unshift(curItem);
      const { ownerKey } = curItem;
      if (ownerKey === null) break;
      curItem = this.items[ownerKey];
    }
    return items;
  }

  async focusField(path: string) {
    const items = this.getOwnerItems(path);
    for (let i = 0; i < items.length - 1; i++) {
      const mainItem = items[i];
      const subItem = items[i + 1];
      if (mainItem && subItem) {
        await mainItem.activateSubItem(subItem);
      }
    }
    const lastItem = items[items.length - 1];
    if (lastItem?.meta.type === "field") {
      await (lastItem as FormField).focus();
    }
  }
}

const validationCounter: DebounceCounter = {};
